import type { HazardClassKey } from '../../pages/acceptanceProof/hazardClassReportOptions';
import { isHazardClassKey } from '../../pages/acceptanceProof/hazardClassReportOptions';

const MOBILE_EQUIPMENT_POLICY_TYPES = new Set([
  'forklift_proximity',
  'pedestrian_near_moving_forklift',
  'pedestrian_near_moving_forklift_v2',
  'forklift_path_conflict',
  'reverse_motion_pedestrian_conflict',
  'pedestrian_in_dynamic_hazard_envelope',
]);

const RESTRICTED_ZONE_POLICY_TYPES = new Set([
  'zone_intrusion',
  'region_entrance',
  'region_exiting',
  'loitering',
  'dwell_violation',
  'line_crossing',
]);

const RESTRICTED_ZONE_INCIDENT_TYPES = new Set([
  'zone_violation',
  'zone_intrusion',
  'intrusion',
  'region_entrance',
  'region_exiting',
  'loitering',
]);

const PPE_POLICY_TYPES = new Set(['ppe_compliance']);
const PPE_INCIDENT_TYPES = new Set(['ppe_violation', 'ppe_violation_person']);

const WORKER_DOWN_POLICY_TYPES = new Set(['worker_down']);
const WORKER_DOWN_INCIDENT_TYPES = new Set(['worker_down']);

const CONTEXTUAL_POLICY_TYPES = new Set([
  'pedestrian_near_moving_forklift_v2',
  'forklift_path_conflict',
  'reverse_motion_pedestrian_conflict',
  'pedestrian_in_dynamic_hazard_envelope',
]);

function norm(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function metricsDict(row: Record<string, unknown>): Record<string, unknown> {
  const metrics = row.metrics;
  return metrics && typeof metrics === 'object' ? (metrics as Record<string, unknown>) : {};
}

function policyType(row: Record<string, unknown>): string {
  const metrics = metricsDict(row);
  return norm(row.policy_type || metrics.policy_type);
}

function incidentType(row: Record<string, unknown>): string {
  const metrics = metricsDict(row);
  return norm(row.incident_type || metrics.incident_type);
}

function packKey(row: Record<string, unknown>): string {
  const metrics = metricsDict(row);
  for (const candidate of [row.pack_key, row.policy_pack_key, metrics.pack_key, metrics.policy_pack_key]) {
    const text = norm(candidate);
    if (text) return text;
  }
  return '';
}

function isForkliftPackContext(row: Record<string, unknown>): boolean {
  const pack = packKey(row);
  if (pack === 'forklift_safety' || pack === 'forklift-safety' || pack === 'forklift' || pack.includes('forklift')) {
    return true;
  }
  const zoneSemantic = norm(row.zone_semantic_hint || metricsDict(row).zone_semantic_hint);
  return zoneSemantic === 'forklift_only' || zoneSemantic === 'forklift-only' || zoneSemantic === 'forklift';
}

/**
 * Map incident / group row to v1 hazard-class key (aligned with cloud hazard_class_registry).
 * Returns null when the row cannot be classified into a supported family.
 */
export function resolveIncidentHazardClassKey(
  source: Record<string, unknown> | null | undefined,
): HazardClassKey | null {
  if (!source || typeof source !== 'object') return null;
  const row = source as Record<string, unknown>;
  const policy = policyType(row);
  const incident = incidentType(row);

  if (PPE_POLICY_TYPES.has(policy) || PPE_INCIDENT_TYPES.has(incident)) {
    return 'ppe_compliance';
  }
  if (WORKER_DOWN_POLICY_TYPES.has(policy) || WORKER_DOWN_INCIDENT_TYPES.has(incident)) {
    return 'worker_down_response';
  }
  if (MOBILE_EQUIPMENT_POLICY_TYPES.has(policy) || CONTEXTUAL_POLICY_TYPES.has(policy)) {
    return 'mobile_equipment_separation';
  }
  if (policy === 'zone_intrusion' || policy === 'dwell_violation') {
    return isForkliftPackContext(row) ? 'mobile_equipment_separation' : 'restricted_zone_intrusion';
  }
  if (policy === 'line_crossing') {
    return isForkliftPackContext(row) ? 'mobile_equipment_separation' : null;
  }
  if (RESTRICTED_ZONE_POLICY_TYPES.has(policy)) {
    return 'restricted_zone_intrusion';
  }
  if (RESTRICTED_ZONE_INCIDENT_TYPES.has(incident) && !MOBILE_EQUIPMENT_POLICY_TYPES.has(policy)) {
    if (isForkliftPackContext(row) && (incident === 'zone_violation' || incident === 'zone_intrusion')) {
      return 'mobile_equipment_separation';
    }
    return 'restricted_zone_intrusion';
  }
  if ((policy === '' || policy === 'unknown') && RESTRICTED_ZONE_INCIDENT_TYPES.has(incident)) {
    return isForkliftPackContext(row) ? 'mobile_equipment_separation' : 'restricted_zone_intrusion';
  }
  return null;
}

export type HazardClassReportPathScope = {
  orgId?: string | null;
  siteId?: string | null;
  reportFrom?: string | null;
  reportTo?: string | null;
  deviceId?: string | null;
  zoneId?: string | null;
};

export function buildHazardClassReportPath(
  hazardClassKey: string,
  scope?: HazardClassReportPathScope,
): string {
  const key = String(hazardClassKey || '').trim().toLowerCase();
  if (!isHazardClassKey(key)) {
    return '/acceptance/proof?report_type=hazard_class';
  }
  const query = new URLSearchParams();
  query.set('report_type', 'hazard_class');
  query.set('hazard_class', key);
  if (scope?.orgId) query.set('org_id', String(scope.orgId).trim());
  if (scope?.siteId) query.set('site_id', String(scope.siteId).trim());
  if (scope?.reportFrom) query.set('report_from', String(scope.reportFrom).trim());
  if (scope?.reportTo) query.set('report_to', String(scope.reportTo).trim());
  if (scope?.deviceId) query.set('device_id', String(scope.deviceId).trim());
  if (scope?.zoneId) query.set('zone_id', String(scope.zoneId).trim());
  return `/acceptance/proof?${query.toString()}`;
}
